import { date, money, qty, time } from '../lib/format';

export interface InvoiceLine {
  id: string;
  description: string;
  hsn: string;
  quantity: number;
  unitPrice: number;
  gstRate: number;
  discount: number;
  taxable: number;
  cgst: number;
  sgst: number;
  igst: number;
  total: number;
}

export interface Invoice {
  id: string;
  number: string;
  kind: 'FNB' | 'ROOM';
  status: string;
  guestName: string;
  guestGstin: string | null;
  placeOfSupply: string;
  isInterState: boolean;
  mode: string;
  issuedAt: string;
  ticketNumber: string | null;
  tableCode: string | null;
  roomNumber: string | null;
  subtotal: number;
  discount: number;
  cgst: number;
  sgst: number;
  igst: number;
  roundOff: number;
  total: number;
  property: {
    name: string;
    address: string;
    city: string;
    gstin: string;
    phone: string;
    fssai: string | null;
    stateCode: string;
  };
  lines: InvoiceLine[];
  slabs: Array<{ gstRate: number; taxable: number; cgst: number; sgst: number; igst: number }>;
}

/** Printable GST tax invoice, shared by POS, front desk and the register. */
export function InvoiceDoc({ invoice }: { invoice: Invoice }) {
  const interState = invoice.isInterState;

  return (
    <div className="doc">
      <div className="doc-head">
        <div>
          <h2>{invoice.property.name}</h2>
          <div className="small">
            {invoice.property.address}, {invoice.property.city}
          </div>
          <div className="small">
            GSTIN {invoice.property.gstin} · {invoice.property.phone}
          </div>
          {invoice.property.fssai ? <div className="small">FSSAI {invoice.property.fssai}</div> : null}
        </div>
        <div className="doc-meta">
          <div style={{ fontSize: 14, fontWeight: 700, color: '#111' }}>TAX INVOICE</div>
          <div>{invoice.number}</div>
          <div>
            {date(invoice.issuedAt)} {time(invoice.issuedAt)}
          </div>
          <div>{invoice.kind === 'ROOM' ? 'Accommodation folio' : 'Restaurant'}</div>
          {invoice.status === 'VOID' ? <div style={{ color: '#b00' }}>VOID</div> : null}
        </div>
      </div>

      <div className="row between small" style={{ marginBottom: 6 }}>
        <div>
          <div>
            <b>Bill to:</b> {invoice.guestName}
          </div>
          {invoice.guestGstin ? <div>GSTIN: {invoice.guestGstin}</div> : null}
          <div>Place of supply: {invoice.placeOfSupply}</div>
        </div>
        <div className="right">
          {invoice.ticketNumber ? (
            <div>
              Ticket {invoice.ticketNumber}
              {invoice.tableCode ? ` · Table ${invoice.tableCode}` : ''}
            </div>
          ) : null}
          {invoice.roomNumber ? <div>Room {invoice.roomNumber}</div> : null}
          <div>Payment: {invoice.mode}</div>
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th style={{ width: 26 }}>#</th>
            <th>Description</th>
            <th>HSN/SAC</th>
            <th className="num">Qty</th>
            <th className="num">Rate</th>
            <th className="num">Taxable</th>
            <th className="num">GST</th>
            <th className="num">Amount</th>
          </tr>
        </thead>
        <tbody>
          {invoice.lines.map((line, index) => (
            <tr key={line.id}>
              <td>{index + 1}</td>
              <td>{line.description}</td>
              <td className="mono">{line.hsn}</td>
              <td className="num">{qty(line.quantity)}</td>
              <td className="num">{money(line.unitPrice)}</td>
              <td className="num">{money(line.taxable)}</td>
              <td className="num">{line.gstRate}%</td>
              <td className="num">{money(line.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="row" style={{ alignItems: 'flex-start', gap: 24 }}>
        <table style={{ flex: 1, maxWidth: 340 }}>
          <thead>
            <tr>
              <th>Slab</th>
              <th className="num">Taxable</th>
              {interState ? (
                <th className="num">IGST</th>
              ) : (
                <>
                  <th className="num">CGST</th>
                  <th className="num">SGST</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {invoice.slabs.map((slab) => (
              <tr key={slab.gstRate}>
                <td>{slab.gstRate}%</td>
                <td className="num">{money(slab.taxable)}</td>
                {interState ? (
                  <td className="num">{money(slab.igst)}</td>
                ) : (
                  <>
                    <td className="num">{money(slab.cgst)}</td>
                    <td className="num">{money(slab.sgst)}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        <table className="tot-table">
          <tbody>
            <tr>
              <td>Taxable value</td>
              <td className="num">{money(invoice.subtotal)}</td>
            </tr>
            {invoice.discount > 0 ? (
              <tr>
                <td>Discount</td>
                <td className="num">− {money(invoice.discount)}</td>
              </tr>
            ) : null}
            {interState ? (
              <tr>
                <td>IGST</td>
                <td className="num">{money(invoice.igst)}</td>
              </tr>
            ) : (
              <>
                <tr>
                  <td>CGST</td>
                  <td className="num">{money(invoice.cgst)}</td>
                </tr>
                <tr>
                  <td>SGST</td>
                  <td className="num">{money(invoice.sgst)}</td>
                </tr>
              </>
            )}
            <tr>
              <td>Round off</td>
              <td className="num">{money(invoice.roundOff)}</td>
            </tr>
            <tr className="grand">
              <td>Total payable</td>
              <td className="num">{money(invoice.total)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="doc-foot">
        <div>
          <div>
            Total GST: {money(invoice.cgst + invoice.sgst + invoice.igst)} ·{' '}
            {interState ? 'Inter-state supply (IGST)' : 'Intra-state supply (CGST + SGST)'}
          </div>
          <div>Declaration: this invoice shows the actual price and all particulars are true.</div>
        </div>
        <div className="right">
          <div style={{ marginTop: 22 }}>Authorised signatory</div>
        </div>
      </div>
    </div>
  );
}
